import { Link } from 'react-router-dom';
import { Movie } from '@/mocks/movies';
import { useState } from 'react';

interface MovieCardProps {
  movie: Movie;
  variant?: 'default' | 'compact' | 'wide';
}

export default function MovieCard({ movie, variant = 'default' }: MovieCardProps) {
  const [imgError, setImgError] = useState(false);

  const getImageUrl = () => {
    const baseUrl = 'https://readdy.ai/api/search-image';
    const seq = `movie-${movie.id}`;
    const query = encodeURIComponent(
      `${movie.title} ${movie.titleEn} movie poster cinematic style, warm tones, dramatic lighting, film grain texture, minimalist composition, dark background with golden accent, high quality`
    );
    if (variant === 'compact') {
      return `${baseUrl}?width=400&height=560&seq=${seq}&orientation=portrait&query=${query}`;
    }
    if (variant === 'wide') {
      return `${baseUrl}?width=800&height=450&seq=${seq}&orientation=landscape&query=${query}`;
    }
    return `${baseUrl}?width=600&height=840&seq=${seq}&orientation=portrait&query=${query}`;
  };

  const getCategoryLabel = (key: string) => {
    const map: Record<string, string> = {
      movie: '电影',
      tv: '电视剧',
      variety: '综艺',
      anime: '动漫',
      doc: '纪录片',
    };
    return map[key] || key;
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group block cursor-pointer"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-2">
          {!imgError ? (
            <img
              src={getImageUrl()}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 在线观看 - 丫丫影院`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
              <i className="ri-film-line text-3xl"></i>
            </div>
          )}
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 text-xs bg-background-900/70 text-background-50 rounded-md">
              {movie.rating}
            </span>
          </div>
          {movie.isNew && (
            <div className="absolute top-2 right-2">
              <span className="px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-md">NEW</span>
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-0.5">
          {getCategoryLabel(movie.category)} · {movie.year}
        </p>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex gap-4 p-3 rounded-lg bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <div className="relative w-32 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
          {!imgError ? (
            <img
              src={getImageUrl()}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 在线观看 - 丫丫影院`}
              className="w-full h-full object-cover"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
              <i className="ri-film-line text-xl"></i>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
            {movie.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{movie.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-foreground-400">{getCategoryLabel(movie.category)}</span>
            <span className="text-xs text-primary-500">{movie.rating}分</span>
            {movie.isNew && (
              <span className="px-1.5 py-0.5 text-xs bg-accent-100 text-accent-700 rounded">NEW</span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-3">
        {!imgError ? (
          <img
            src={getImageUrl()}
            alt={`${movie.title} 海报`}
            title={`${movie.title} 在线观看 - 丫丫影院`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-background-200 text-foreground-400">
            <i className="ri-film-line text-4xl"></i>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <p className="text-xs text-white line-clamp-2">{movie.description}</p>
        </div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          <span className="px-2 py-0.5 text-xs font-semibold bg-primary-500 text-background-50 rounded-md">
            {movie.rating}
          </span>
        </div>
        {movie.isNew && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-0.5 text-xs font-semibold bg-accent-500 text-background-50 rounded-md animate-pulse">
              NEW
            </span>
          </div>
        )}
      </div>
      <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
        {movie.title}
      </h3>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-foreground-500">{getCategoryLabel(movie.category)}</span>
        <span className="text-xs text-foreground-400">·</span>
        <span className="text-xs text-foreground-500">{movie.year}</span>
      </div>
    </Link>
  );
}